import fabric from '@/lib/fabric';
import { isObjectLocked } from '@/lib/layerOps';

export type BooleanMode = 'union' | 'subtract' | 'intersect' | 'exclude';

type Pt = { x: number; y: number };

const UNSUPPORTED = new Set([
  'image',
  'i-text',
  'text',
  'textbox',
  'activeSelection',
  'group',
]);

function zIndex(canvas: fabric.Canvas, obj: fabric.Object): number {
  return canvas.getObjects().indexOf(obj);
}

function sortByStack(canvas: fabric.Canvas, objects: fabric.Object[]): fabric.Object[] {
  return [...objects].sort((a, b) => zIndex(canvas, a) - zIndex(canvas, b));
}

function canBoolean(obj: fabric.Object): boolean {
  const type = (obj.type || '').toLowerCase();
  if (UNSUPPORTED.has(type)) return false;
  if (isObjectLocked(obj)) return false;
  return true;
}

/** Douglas–Peucker simplification */
function simplify(points: Pt[], epsilon: number): Pt[] {
  if (points.length < 3) return points;

  const sq = (p: Pt, a: Pt, b: Pt) => {
    let x = a.x;
    let y = a.y;
    let dx = b.x - x;
    let dy = b.y - y;
    if (dx !== 0 || dy !== 0) {
      const t = ((p.x - x) * dx + (p.y - y) * dy) / (dx * dx + dy * dy);
      if (t > 1) {
        x = b.x;
        y = b.y;
      } else if (t > 0) {
        x += dx * t;
        y += dy * t;
      }
    }
    dx = p.x - x;
    dy = p.y - y;
    return dx * dx + dy * dy;
  };

  const rec = (pts: Pt[], eps2: number): Pt[] => {
    let maxD = 0;
    let idx = 0;
    const end = pts.length - 1;
    for (let i = 1; i < end; i += 1) {
      const d = sq(pts[i], pts[0], pts[end]);
      if (d > maxD) {
        idx = i;
        maxD = d;
      }
    }
    if (maxD > eps2) {
      const left = rec(pts.slice(0, idx + 1), eps2);
      const right = rec(pts.slice(idx), eps2);
      return left.slice(0, -1).concat(right);
    }
    return [pts[0], pts[end]];
  };

  const closed =
    points.length > 2 &&
    Math.abs(points[0].x - points[points.length - 1].x) < 1e-6 &&
    Math.abs(points[0].y - points[points.length - 1].y) < 1e-6;
  const open = closed ? points.slice(0, -1) : points.slice();
  const simplified = rec(open, epsilon * epsilon);
  if (closed && simplified.length) {
    simplified.push({ ...simplified[0] });
  }
  return simplified;
}

/**
 * Trace the outer contour of a binary mask (1 = filled).
 * Returns canvas-space points using the provided pixel→canvas mapper.
 */
function traceOuterContour(
  mask: Uint8Array,
  w: number,
  h: number,
  toCanvas: (px: number, py: number) => Pt
): Pt[] | null {
  // Find a start pixel on the boundary (filled with empty left neighbor)
  let sx = -1;
  let sy = -1;
  outer: for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (mask[y * w + x] && (x === 0 || !mask[y * w + (x - 1)])) {
        sx = x;
        sy = y;
        break outer;
      }
    }
  }
  if (sx < 0) return null;

  // Moore neighborhood clockwise from left
  const dx = [-1, -1, 0, 1, 1, 1, 0, -1];
  const dy = [0, -1, -1, -1, 0, 1, 1, 1];

  const points: Pt[] = [];
  let x = sx;
  let y = sy;
  let dir = 0; // coming from left
  const maxSteps = w * h * 4;

  for (let step = 0; step < maxSteps; step += 1) {
    points.push(toCanvas(x + 0.5, y + 0.5));

    // Start search from dir+6 (prefer turning left / boundary follow)
    let found = false;
    for (let i = 0; i < 8; i += 1) {
      const nd = (dir + 6 + i) % 8;
      const nx = x + dx[nd];
      const ny = y + dy[nd];
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      if (mask[ny * w + nx]) {
        x = nx;
        y = ny;
        dir = nd;
        found = true;
        break;
      }
    }
    if (!found) break;
    if (x === sx && y === sy && points.length > 2) break;
  }

  if (points.length < 3) return null;
  return points;
}

function renderObjectFill(ctx: CanvasRenderingContext2D, obj: fabric.Object): void {
  const prevStroke = obj.stroke;
  const prevStrokeWidth = obj.strokeWidth;
  const prevOpacity = obj.opacity;
  const prevShadow = obj.shadow;
  obj.set({
    stroke: undefined,
    strokeWidth: 0,
    opacity: 1,
    shadow: undefined,
  });
  obj.render(ctx);
  obj.set({
    stroke: prevStroke,
    strokeWidth: prevStrokeWidth,
    opacity: prevOpacity,
    shadow: prevShadow,
  });
}

function compositeMask(
  objects: fabric.Object[],
  mode: BooleanMode,
  bounds: { left: number; top: number; width: number; height: number },
  scale: number
): { mask: Uint8Array; w: number; h: number; pad: number } {
  const pad = 2;
  const w = Math.max(1, Math.ceil(bounds.width * scale) + pad * 2);
  const h = Math.max(1, Math.ceil(bounds.height * scale) + pad * 2);
  const el = document.createElement('canvas');
  el.width = w;
  el.height = h;
  const ctx = el.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas unsupported');

  ctx.setTransform(
    scale,
    0,
    0,
    scale,
    -bounds.left * scale + pad,
    -bounds.top * scale + pad
  );

  const [base, ...rest] = objects;

  if (mode === 'union') {
    objects.forEach((obj) => renderObjectFill(ctx, obj));
  } else if (mode === 'subtract') {
    renderObjectFill(ctx, base);
    ctx.globalCompositeOperation = 'destination-out';
    rest.forEach((obj) => renderObjectFill(ctx, obj));
  } else if (mode === 'intersect') {
    renderObjectFill(ctx, base);
    ctx.globalCompositeOperation = 'destination-in';
    rest.forEach((obj) => renderObjectFill(ctx, obj));
  } else {
    // exclude (xor)
    renderObjectFill(ctx, base);
    ctx.globalCompositeOperation = 'xor';
    rest.forEach((obj) => renderObjectFill(ctx, obj));
  }

  const data = ctx.getImageData(0, 0, w, h).data;
  const mask = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i += 1) {
    mask[i] = data[i * 4 + 3] > 16 ? 1 : 0;
  }
  return { mask, w, h, pad };
}

function selectionBounds(objects: fabric.Object[]) {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  objects.forEach((obj) => {
    const b = obj.getBoundingRect(true);
    left = Math.min(left, b.left);
    top = Math.min(top, b.top);
    right = Math.max(right, b.left + b.width);
    bottom = Math.max(bottom, b.top + b.height);
  });
  return {
    left,
    top,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
  };
}

/**
 * Boolean pathfinder on the active selection.
 * Back-most object is the base for subtract/intersect/exclude.
 * Result is a single Polygon (vector outline of the composite).
 */
export function applyBooleanSelection(
  canvas: fabric.Canvas,
  mode: BooleanMode
): { ok: boolean; note: string } {
  const raw = canvas.getActiveObjects();
  if (raw.length < 2) {
    return { ok: false, note: 'Select 2+ shapes to combine' };
  }

  const objects = sortByStack(
    canvas,
    raw.filter(canBoolean)
  );
  if (objects.length < 2) {
    return {
      ok: false,
      note: 'Need 2+ unlocked shapes (not text, image, or groups)',
    };
  }

  const bounds = selectionBounds(objects);
  const scale = 2;
  const { mask, w, h, pad } = compositeMask(objects, mode, bounds, scale);

  const toCanvas = (px: number, py: number): Pt => ({
    x: bounds.left + (px - pad) / scale,
    y: bounds.top + (py - pad) / scale,
  });

  const contour = traceOuterContour(mask, w, h, toCanvas);
  if (!contour) {
    return { ok: false, note: 'Boolean result was empty' };
  }

  const simplified = simplify(contour, 0.75 / scale);
  // Drop closing duplicate for fabric.Polygon
  const pts =
    simplified.length > 1 &&
    Math.abs(simplified[0].x - simplified[simplified.length - 1].x) < 1e-4 &&
    Math.abs(simplified[0].y - simplified[simplified.length - 1].y) < 1e-4
      ? simplified.slice(0, -1)
      : simplified;

  if (pts.length < 3) {
    return { ok: false, note: 'Boolean result was empty' };
  }

  const base = objects[0];
  const fill =
    typeof base.fill === 'string' && base.fill ? base.fill : '#4a90d9';
  const stroke = typeof base.stroke === 'string' ? base.stroke : undefined;
  const strokeWidth = base.strokeWidth || 0;

  const minX = Math.min(...pts.map((p) => p.x));
  const minY = Math.min(...pts.map((p) => p.y));
  const localPts = pts.map((p) => ({ x: p.x - minX, y: p.y - minY }));

  const poly = new fabric.Polygon(localPts, {
    left: minX,
    top: minY,
    fill,
    stroke,
    strokeWidth,
    opacity: base.opacity ?? 1,
    originX: 'left',
    originY: 'top',
    objectCaching: true,
  });
  (poly as fabric.Object & { name?: string }).name =
    mode === 'union'
      ? 'Union'
      : mode === 'subtract'
        ? 'Subtract'
        : mode === 'intersect'
          ? 'Intersect'
          : 'Exclude';

  canvas.discardActiveObject();
  objects.forEach((obj) => canvas.remove(obj));
  canvas.add(poly);
  canvas.setActiveObject(poly);
  canvas.requestRenderAll();

  return { ok: true, note: `Applied ${mode}` };
}
